import { useState } from "react"
import Grid from "@mui/material/Grid"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDNumberInput from "../MDNumberInput"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import EmailChipInput from "./EmailChipInput"
import {
  APPROVAL_MODE_OPTIONS,
  approvalModeLabel,
  formatQuorum,
  findApproverOption,
  dynamicApproverOption,
} from "../../constants"

const ApprovalStepTimeline = ({
  steps,
  conditions = [],
  approverOptions,
  onChangeStep,
  onRemoveStep,
  onReorder,
}) => {
  const [dragIdx, setDragIdx] = useState(null)

  const handleDragStart = (idx) => setDragIdx(idx)
  const handleDragOver = (e) => e.preventDefault()
  const handleDrop = (targetIdx) => {
    if (dragIdx == null || dragIdx === targetIdx) return
    onReorder(dragIdx, targetIdx)
    setDragIdx(null)
  }

  return (
    <MDBox>
      {steps.map((s, idx) => {
        const isLast = idx === steps.length - 1
        const isEmails = s.approverType === "emails"
        const selectedApprover = findApproverOption(s)
        const dynamic = dynamicApproverOption(s.approverType)
        const missingCondition =
          dynamic &&
          !conditions.some(
            (c) => c.field === dynamic.conditionField && (c.operator === "=" || c.operator === "in")
          )
        const approverLabel = isEmails
          ? s.approverRef || "No email added yet"
          : selectedApprover?.label || s.approverRef
        const emailCount = isEmails
          ? (s.approverRef || "")
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean).length
          : 0

        return (
          <MDBox
            key={s.stepOrder || idx}
            display="flex"
            gap={1.5}
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(idx)}
            onDragEnd={() => setDragIdx(null)}
            sx={{
              opacity: dragIdx === idx ? 0.5 : 1,
              cursor: "grab",
            }}
          >
            <MDBox display="flex" flexDirection="column" alignItems="center" width="2rem" pt={1}>
              <MDBox
                width="2rem"
                height="2rem"
                borderRadius="50%"
                bgColor="info"
                color="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <MDTypography variant="caption" color="white" fontWeight="bold">
                  {idx + 1}
                </MDTypography>
              </MDBox>
              {!isLast && (
                <MDBox width="2px" flex={1} minHeight="2rem" bgColor="light" my={0.5} />
              )}
            </MDBox>

            <MDBox flex={1} pb={isLast ? 0 : 2} sx={{ minWidth: 0 }}>
              <MDBox p={2} borderRadius="lg" bgColor="grey-100">
                <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="small" sx={{ color: "text.secondary", cursor: "grab" }}>
                      drag_indicator
                    </Icon>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      Step {idx + 1}
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    size="small"
                    variant="text"
                    color="error"
                    iconOnly
                    onClick={() => onRemoveStep(idx)}
                  >
                    <Icon fontSize="small">delete</Icon>
                  </MDButton>
                </MDBox>

                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={4}>
                    <MDSelect
                      labelType="non-sticky"
                      labelText="Approver"
                      options={approverOptions}
                      value={selectedApprover || null}
                      onChange={(o) =>
                        onChangeStep(idx, {
                          approverType: o?.type,
                          approverRef: o?.type === "emails" ? (isEmails ? s.approverRef : "") : o?.value,
                        })
                      }
                    />
                    {isEmails && (
                      <MDBox mt={1.5}>
                        <MDTypography
                          variant="caption"
                          display="block"
                          mb={0.5}
                        >
                          Approver emails
                        </MDTypography>
                        <EmailChipInput
                          value={s.approverRef || ""}
                          onChange={(next) => onChangeStep(idx, { approverRef: next })}
                          placeholder="name1@company.com, name2@company.com"
                        />
                        <MDTypography
                          variant="caption"
                          color="text"
                          display="block"
                          mt={0.5}
                          sx={{ opacity: 0.8 }}
                        >
                          Type an address then comma / space / Enter to confirm it. Click a
                          badge to edit it, or its × to remove.
                        </MDTypography>
                      </MDBox>
                    )}
                    {dynamic && (
                      <MDTypography
                        variant="caption"
                        color={missingCondition ? "error" : "text"}
                        display="block"
                        mt={0.5}
                      >
                        Needs a rule condition on {dynamic.conditionField} (= or in).
                      </MDTypography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MDSelect
                      labelType="non-sticky"
                      labelText="How they approve"
                      options={APPROVAL_MODE_OPTIONS}
                      value={
                        APPROVAL_MODE_OPTIONS.find((o) => o.value === s.mode) || null
                      }
                      onChange={(o) => onChangeStep(idx, { mode: o?.value })}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDNumberInput
                      fullWidth
                      labelType="non-sticky"
                      labelText="Quorum"
                      value={s.quorum}
                      onChange={(v) => onChangeStep(idx, { quorum: Number(v) })}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <MDNumberInput
                      fullWidth
                      labelType="non-sticky"
                      labelText="SLA hours"
                      value={s.slaHours}
                      onChange={(v) => onChangeStep(idx, { slaHours: Number(v) })}
                    />
                  </Grid>
                </Grid>

                <MDTypography
                  variant="caption"
                  color="text"
                  display="block"
                  mt={1}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* Emails already show as chips in the field above — a
                      long comma-separated address list here would just
                      overflow the card, so summarize by count instead. */}
                  {isEmails ? `${emailCount} email${emailCount === 1 ? "" : "s"}` : approverLabel}{" "}
                  · {approvalModeLabel(s.mode)} · {formatQuorum(s.quorum)}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        )
      })}
    </MDBox>
  )
}

export default ApprovalStepTimeline
